/** @format */
import { inject, Injectable } from "@angular/core";
import { ApiService } from "@corelib";
import { Observable } from "rxjs";
import {
  EvaluationPrograms,
  ExpertCabinet,
  ExpertCabinetQuery,
  ExpertWorkspace,
  AssignmentOptions,
  AllocationQuery,
  AllocationPlan,
  QuotaPreview,
  ExpertInvitation,
  InvitationAccepted,
  ExpertRegistration,
  ExpertRegistered,
  ExpertCaseScopeWrite,
  ProjectExpertsWrite,
  ExpertsBulkWrite,
} from "@domain/program/evaluation-workspace.model";
import { EvaluationRepositoryPort } from "@domain/program/ports/evaluation.repository.port";
@Injectable({ providedIn: "root" })
export class EvaluationHttpAdapter implements EvaluationRepositoryPort {
  private readonly api = inject(ApiService);
  private root(id: number) {
    return `/programs/${id}/management`;
  }
  programs(): Observable<EvaluationPrograms> {
    return this.api.get("/evaluation/programs/");
  }
  cabinet(query: ExpertCabinetQuery): Observable<ExpertCabinet> {
    const params = new URLSearchParams({
      include: "projects",
      status: query.status,
      limit: String(query.limit),
      offset: String(query.offset),
    });
    if (query.q) params.set("q", query.q);
    if (query.programId) params.set("program_id", String(query.programId));
    return this.api.get(`/evaluation/programs/?${params}`);
  }
  workspace(id: number): Observable<ExpertWorkspace> {
    return this.api.get(`${this.root(id)}/experts/`);
  }
  bulkExperts(id: number, data: ExpertsBulkWrite): Observable<ExpertWorkspace> {
    return this.api.post(`${this.root(id)}/experts/bulk/`, data);
  }
  saveCaseScope(
    id: number,
    expertId: number,
    data: ExpertCaseScopeWrite,
  ): Observable<ExpertWorkspace> {
    return this.api.put(`${this.root(id)}/experts/${expertId}/cases/`, data);
  }
  saveProjectExperts(id: number, data: ProjectExpertsWrite): Observable<ExpertWorkspace> {
    return this.api.post(`${this.root(id)}/project-experts/`, data);
  }
  invite(
    id: number,
    email: string,
    scope?: Omit<ExpertCaseScopeWrite, "revision">,
  ): Observable<unknown> {
    return this.api.post(`${this.root(id)}/experts/`, { email, ...scope });
  }
  saveInvitationCaseScope(
    id: number,
    invitationId: number,
    data: ExpertCaseScopeWrite,
  ): Observable<ExpertWorkspace> {
    return this.api.post(`${this.root(id)}/expert-invitations/${invitationId}/cases/`, data);
  }
  invitationAction(
    id: number,
    invitationId: number,
    action: "resend" | "revoke",
  ): Observable<unknown> {
    return this.api.post(`${this.root(id)}/expert-invitations/${invitationId}/${action}/`, {});
  }
  assignments(id: number, expertId: number): Observable<AssignmentOptions> {
    return this.api.get(`${this.root(id)}/experts/${expertId}/assignments/`);
  }
  saveAssignments(
    id: number,
    expertId: number,
    revision: string,
    projectIds: number[],
  ): Observable<AssignmentOptions> {
    return this.api.put(`${this.root(id)}/experts/${expertId}/assignments/`, {
      revision,
      projectIds,
    });
  }
  allocation(id: number, query: AllocationQuery): Observable<AllocationPlan> {
    return this.api.post(`${this.root(id)}/expert-allocation/`, query);
  }
  applyAllocation(id: number, planToken: string): Observable<ExpertWorkspace> {
    return this.api.post(`${this.root(id)}/expert-allocation/apply/`, { planToken });
  }
  quota(id: number, requiredEvaluators: number): Observable<QuotaPreview> {
    return this.api.post(`${this.root(id)}/evaluators/`, { requiredEvaluators });
  }
  saveQuota(id: number, requiredEvaluators: number, revision: string): Observable<ExpertWorkspace> {
    return this.api.put(`${this.root(id)}/evaluators/`, { requiredEvaluators, revision });
  }
  invitation(token: string): Observable<ExpertInvitation> {
    return this.api.get(`/evaluation/invitations/${encodeURIComponent(token)}/`);
  }
  accept(token: string): Observable<InvitationAccepted> {
    return this.api.post(`/evaluation/invitations/${encodeURIComponent(token)}/accept/`, {});
  }
  acceptOwn(id: number): Observable<InvitationAccepted> {
    return this.api.post(`/evaluation/own-invitations/${id}/accept/`, {});
  }
  register(token: string, data: ExpertRegistration): Observable<ExpertRegistered> {
    return this.api.post(`/evaluation/invitations/${encodeURIComponent(token)}/register/`, data);
  }
  resetPassword(token: string, email: string): Observable<void> {
    return this.api.post("/auth/reset_password/", { email, expertInvitationToken: token });
  }
}
